// admin-web/src/pages/clubs/ClubsPage.tsx
import {
    addDoc,
    collection,
    doc,
    getDocs,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { db, functions } from "../../firebase";

type ClubDoc = {
  name: string;
  description: string;
  category: string;
  meetingSchedule: string;
  advisorName: string;
  advisorEmail: string;
  isActive: boolean;
  messagingMode?: "broadcast" | "group";
  threadId?: string | null;
  createdAt?: any;
  updatedAt?: any;
};

type Row = { id: string } & ClubDoc;

function norm(s: string) {
  return String(s || "").trim();
}
function normEmail(s: string) {
  return String(s || "").trim().toLowerCase();
}

export function ClubsPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [filter, setFilter] = useState("");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [meetingSchedule, setMeetingSchedule] = useState("");
  const [advisorName, setAdvisorName] = useState("");
  const [advisorEmail, setAdvisorEmail] = useState("");
  const [saving, setSaving] = useState(false);

  // Quick leader invite UI state
  const [leaderEmail, setLeaderEmail] = useState("");
  const [leaderClubId, setLeaderClubId] = useState<string | null>(null);
  const [invitingLeader, setInvitingLeader] = useState(false);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return rows;
    return rows.filter((r) => {
      const hay = `${r.name} ${r.category} ${r.advisorName} ${r.advisorEmail}`.toLowerCase();
      return hay.includes(f);
    });
  }, [rows, filter]);

  async function load() {
    setLoading(true);
    setErr(null);
    setOk(null);
    try {
      const qRef = query(collection(db, "clubs"), orderBy("name", "asc"));
      const snap = await getDocs(qRef);
      const out: Row[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as ClubDoc) }));
      setRows(out);
    } catch (e: any) {
      setErr(e?.message || "Failed to load clubs.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function addClub() {
    setErr(null);
    setOk(null);
    setSaving(true);
    try {
      const docData: ClubDoc = {
        name: norm(name),
        description: norm(description),
        category: norm(category),
        meetingSchedule: norm(meetingSchedule),
        advisorName: norm(advisorName),
        advisorEmail: normEmail(advisorEmail),
        isActive: true,
        messagingMode: "broadcast",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (!docData.name) {
        throw new Error("Club name is required.");
      }

      await addDoc(collection(db, "clubs"), docData);

      setName("");
      setDescription("");
      setCategory("");
      setMeetingSchedule("");
      setAdvisorName("");
      setAdvisorEmail("");

      setOk("Club created.");
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to create club.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row: Row) {
    setErr(null);
    setOk(null);
    try {
      await updateDoc(doc(db, "clubs", row.id), {
        isActive: !row.isActive,
        updatedAt: serverTimestamp(),
      });
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to update club.");
    }
  }

  function beginQuickLeaderInvite(clubId: string) {
    setErr(null);
    setOk(null);
    setLeaderClubId(clubId);
    setLeaderEmail("");
  }

  function cancelQuickLeaderInvite() {
    setLeaderClubId(null);
    setLeaderEmail("");
  }

  async function submitQuickLeaderInvite() {
    setErr(null);
    setOk(null);

    const clubId = leaderClubId;
    const email = normEmail(leaderEmail);

    if (!clubId) {
      setErr("No club selected for leader invite.");
      return;
    }
    if (!email || !email.includes("@")) {
      setErr("Enter a valid leader email address.");
      return;
    }

    setInvitingLeader(true);
    try {
      const createInvite = httpsCallable(functions, "createClubInvite");
      const res = await createInvite({
        clubId,
        email,
        role: "leader",
      });

      const data: any = res.data || {};
      const reused = data.reused === true;

      setOk(
        reused
          ? `Leader invite already existed for ${email}.`
          : `Leader invite created for ${email}.`
      );

      // keep the modal open only if you want; we’ll close by default
      cancelQuickLeaderInvite();
    } catch (e: any) {
      setErr(e?.message || "Failed to create leader invite.");
    } finally {
      setInvitingLeader(false);
    }
  }

  const leaderClubName = useMemo(() => {
    if (!leaderClubId) return "";
    return rows.find((r) => r.id === leaderClubId)?.name || leaderClubId;
  }, [leaderClubId, rows]);

  return (
    <div className="card">
      <h2>Clubs</h2>
      <p className="muted">Create clubs and manage membership/leaders.</p>
      <p className="muted">New clubs use leader announcements. Each active leader gets a separate thread in Messages; members can read announcements but cannot reply.</p>

      {err ? <div className="error">{err}</div> : null}
      {ok ? <div className="success">{ok}</div> : null}

      <div className="grid2">
        <div>
          <label className="label">Search</label>
          <input
            className="input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Chess, Robotics, advisor…"
          />
        </div>
      </div>

      <hr className="hr" />

      <h3>Add Club</h3>
      <div className="grid2">
        <div>
          <label className="label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="label">Category</label>
          <input
            className="input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Academic / Sports / Arts…"
          />
        </div>
        <div>
          <label className="label">Meeting Schedule</label>
          <input
            className="input"
            value={meetingSchedule}
            onChange={(e) => setMeetingSchedule(e.target.value)}
            placeholder="Wednesdays 3:30–4:30"
          />
        </div>
        <div>
          <label className="label">Advisor Name</label>
          <input
            className="input"
            value={advisorName}
            onChange={(e) => setAdvisorName(e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div>
          <label className="label">Advisor Email</label>
          <input
            className="input"
            value={advisorEmail}
            onChange={(e) => setAdvisorEmail(e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label className="label">Description</label>
          <input
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description…"
          />
        </div>
      </div>

      <button className="btn" disabled={saving} onClick={addClub} style={{ marginTop: 10 }}>
        {saving ? "Saving…" : "Create Club"}
      </button>

      <hr className="hr" />

      <h3>Existing Clubs</h3>

      {loading ? (
        <div className="muted">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="muted">No clubs found.</div>
      ) : (
        <div className="table">
          <div className="thead" style={{ gridTemplateColumns: "1fr 140px 140px 180px 230px" }}>
            <div>Club</div>
            <div>Status</div>
            <div>Chat</div>
            <div>Members</div>
            <div></div>
          </div>

          {filtered.map((r) => (
            <div
              className="trow"
              key={r.id}
              style={{ gridTemplateColumns: "1fr 140px 140px 180px 230px" }}
            >
              <div>
                <div style={{ fontWeight: 800 }}>{r.name}</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {r.category ? r.category : "Uncategorized"}
                </div>
              </div>

              <div>
                <span className={r.isActive ? "pill ok" : "pill off"}>
                  {r.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="muted" style={{ fontSize: 12 }}>
                {r.threadId ? "Chat ready" : "Not created"}
              </div>

              <div>
                <Link to={`/clubs/${r.id}/members`} className="btn small secondary">
                  Manage Members
                </Link>
              </div>

              <div style={{ textAlign: "right", display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button className="btn small secondary" onClick={() => beginQuickLeaderInvite(r.id)}>
                  Quick Invite Leader
                </button>
                <button className="btn small secondary" onClick={() => toggleActive(r)}>
                  {r.isActive ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick leader invite modal */}
      {leaderClubId ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 9999,
          }}
          onClick={cancelQuickLeaderInvite}
        >
          <div
            className="card"
            style={{ width: "min(720px, 96vw)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Quick Invite Leader</h3>
            <p className="muted" style={{ marginTop: 6 }}>
              Club: <b>{leaderClubName}</b>
            </p>
            <p className="muted" style={{ marginTop: 6 }}>
              This creates a <b>leader</b> invite for the email. If the person already has an account,
              they can accept immediately; otherwise they’ll accept after signing in.
            </p>

            <label className="label" style={{ marginTop: 10 }}>
              Leader Email
            </label>
            <input
              className="input"
              value={leaderEmail}
              onChange={(e) => setLeaderEmail(e.target.value)}
              placeholder="leader@example.com"
              autoFocus
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
              <button className="btn secondary" onClick={cancelQuickLeaderInvite} disabled={invitingLeader}>
                Cancel
              </button>
              <button className="btn" onClick={submitQuickLeaderInvite} disabled={invitingLeader}>
                {invitingLeader ? "Inviting…" : "Create Leader Invite"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
